import { createFileRoute, Link } from "@tanstack/react-router";
import { Download, Printer } from "lucide-react";
import { useMemo, useState } from "react";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Panel, RiskBadge, StatusChip } from "@/lovable-demo/components/bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { downloadCsv, exportPdf } from "@/lovable-demo/lib/warranty/export";
import {
  fmtCompactCurrency,
  fmtDate,
  recordDelayCost,
  recordNpr,
  statusLabel,
  workflowProgress,
} from "@/lovable-demo/lib/warranty/calc";
import { recordsForProgram } from "@/lovable-demo/lib/warranty/aggregate";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/problem-records")({
  head: () => ({
    meta: [
      { title: "Problem Record Registry — Broadview Warranty Intelligence" },
      {
        name: "description",
        content:
          "Filter and sort governed Problem Records by risk, status and financial exposure, then export the registry to CSV or PDF.",
      },
      { property: "og:title", content: "Problem Record Registry — Broadview" },
      {
        property: "og:description",
        content:
          "Filter and sort governed Problem Records by risk, status and financial exposure, then export the registry to CSV or PDF.",
      },
    ],
  }),
  component: Registry,
});

type SortKey = "risk" | "status" | "exposure" | "npr" | "code";

function Registry() {
  const { data, activeProgramId } = useWarranty();
  const all = recordsForProgram(data, activeProgramId);
  const [query, setQuery] = useState("");
  const [risk, setRisk] = useState("all");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState<SortKey>("risk");
  const [dir, setDir] = useState<"asc" | "desc">("desc");

  const rows = useMemo(() => {
    const filtered = all.filter((r) => {
      const q = query.trim().toLowerCase();
      const matchQ =
        !q ||
        [r.code, r.title, r.component, r.supplier, r.owner, r.failureCause]
          .join(" ")
          .toLowerCase()
          .includes(q);
      return (
        matchQ && (risk === "all" || r.risk === risk) && (status === "all" || r.status === status)
      );
    });
    const val = (r: (typeof all)[number]) =>
      sort === "risk"
        ? r.riskScore
        : sort === "exposure"
          ? r.financial.exposure
          : sort === "npr"
            ? recordNpr(r)
            : sort === "status"
              ? (statusLabel[r.status] ?? r.status)
              : r.code;
    return [...filtered].sort((a, b) => {
      const av = val(a);
      const bv = val(b);
      const cmp =
        typeof av === "number" && typeof bv === "number"
          ? av - bv
          : String(av).localeCompare(String(bv));
      return dir === "asc" ? cmp : -cmp;
    });
  }, [all, query, risk, status, sort, dir]);

  const setSorting = (key: SortKey) => {
    if (key === sort) setDir(dir === "asc" ? "desc" : "asc");
    else {
      setSort(key);
      setDir(key === "code" || key === "status" ? "asc" : "desc");
    }
  };

  const csv = () =>
    downloadCsv(
      "broadview-problem-records.csv",
      rows.map((r) => ({
        Code: r.code,
        Title: r.title,
        Program: data.programs.find((p) => p.id === r.programId)?.name ?? "",
        Component: r.component,
        Supplier: r.supplier,
        Status: statusLabel[r.status] ?? r.status,
        Risk: r.risk,
        "Risk score": r.riskScore,
        Claims: r.claims,
        "Actual cost": r.financial.actualCost,
        Exposure: r.financial.exposure,
        "Remaining exposure": recordNpr(r),
        "Delay cost / month": Math.round(recordDelayCost(r)),
        "Stage progress": `${workflowProgress(r.stages).done}/11`,
        Owner: r.owner,
        Opened: r.openedAt.slice(0, 10),
        "Target close": r.targetCloseAt.slice(0, 10),
      })),
    );

  const th = (key: SortKey, label: string, className = "") => (
    <th className={`px-3 py-2 text-left font-medium ${className}`}>
      <button onClick={() => setSorting(key)} className="hover:text-foreground">
        {label}
        {sort === key ? (dir === "asc" ? " ▲" : " ▼") : ""}
      </button>
    </th>
  );

  const program = data.programs.find((p) => p.id === activeProgramId);
  const programLabel = program
    ? `${program.code} · ${program.name} · MY${program.modelYear}`
    : "All programs · consolidated portfolio";

  return (
    <AppShell
      title="Problem Record Registry"
      subtitle={`${programLabel} — ${rows.length} of ${all.length} governed records`}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={csv}>
            <Download className="size-3.5" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={exportPdf}>
            <Printer className="size-3.5" /> PDF
          </Button>
        </>
      }
    >
      <Panel>
        <div className="no-print mb-4 flex flex-wrap items-center gap-2">
          <Input
            placeholder="Search code, title, component, supplier…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-9 max-w-xs bg-elevated"
          />
          <select
            value={risk}
            onChange={(e) => setRisk(e.target.value)}
            className="h-9 rounded border border-input bg-elevated px-2 text-sm"
          >
            <option value="all">All risk</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="h-9 rounded border border-input bg-elevated px-2 text-sm"
          >
            <option value="all">All statuses</option>
            {Object.entries(statusLabel).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px] text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {th("code", "Record")}
                <th className="px-3 py-2 text-left font-medium">Component / supplier</th>
                {th("status", "Status")}
                {th("risk", "Risk")}
                <th className="px-3 py-2 text-right font-medium">Claims</th>
                {th("exposure", "Exposure", "text-right")}
                {th("npr", "Remaining exposure", "text-right")}
                <th className="px-3 py-2 text-right font-medium">Delay / mo</th>
                <th className="px-3 py-2 text-left font-medium">Stage</th>
                <th className="px-3 py-2 text-left font-medium">Target close</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {rows.map((r) => {
                const prog = workflowProgress(r.stages);
                return (
                  <tr key={r.id} className="transition-colors hover:bg-elevated/60">
                    <td className="px-3 py-2.5">
                      <Link
                        to="/demo/problem-record/$id"
                        params={{ id: r.id }}
                        className="block max-w-[260px] font-medium text-foreground hover:text-primary"
                      >
                        <span className="num block text-xs text-primary">{r.code}</span>
                        <span className="block truncate">{r.title}</span>
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      <span className="block text-foreground">{r.component}</span>
                      <span className="block text-xs">{r.supplier}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <StatusChip status={r.status} />
                    </td>
                    <td className="px-3 py-2.5">
                      <RiskBadge risk={r.risk} score={r.riskScore} />
                    </td>
                    <td className="num px-3 py-2.5 text-right">{r.claims.toLocaleString()}</td>
                    <td className="num px-3 py-2.5 text-right">
                      {fmtCompactCurrency(r.financial.exposure)}
                    </td>
                    <td className="num px-3 py-2.5 text-right text-success">
                      {fmtCompactCurrency(recordNpr(r))}
                    </td>
                    <td className="num px-3 py-2.5 text-right text-warning">
                      {fmtCompactCurrency(recordDelayCost(r))}
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-16 overflow-hidden rounded bg-elevated">
                          <div className="h-full bg-primary" style={{ width: `${prog.pct}%` }} />
                        </div>
                        <span className="num text-xs text-muted-foreground">{prog.done}/11</span>
                      </div>
                    </td>
                    <td className="num px-3 py-2.5 text-xs text-muted-foreground">
                      {fmtDate(r.targetCloseAt)}
                    </td>
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-3 py-8 text-center text-muted-foreground">
                    No records match the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Panel>
    </AppShell>
  );
}
